import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import authAxios from '../../../lib/authAxios';
import { useAuth } from '../../../contexts/AuthContext';
import StudentConsentModal from './StudentConsentModal';

const LoginForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lockoutSeconds, setLockoutSeconds] = useState(0);
  const [showConsent, setShowConsent] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, updateUser } = useAuth();
  const { register, handleSubmit, setError: setFormFieldError, formState: { errors } } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit'
  });

  useEffect(() => {
    if (lockoutSeconds <= 0) return;
    const interval = setInterval(() => {
      setLockoutSeconds((prev) => {
        if (prev <= 1) {
          setError(null);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [lockoutSeconds]);

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await authAxios.post('/login', data);
      const { accessToken, user, csrfToken } = response.data.data;

      // Secure: store token in-memory, user metadata in localStorage
      login(accessToken, user, csrfToken);

      const fromLocation = location.state?.from;
      const defaultTarget = user.role === 'student' ? '/dashboard' : '/feed';
      const redirectPath = fromLocation ? `${fromLocation.pathname}${fromLocation.search || ''}` : defaultTarget;

      if (user.role === 'student' && !user.consentAcceptedAt) {
        setShowConsent(true);
      } else {
        navigate(redirectPath, { replace: true });
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Invalid email or password';
      const isLockout =
        err.response?.status === 429 ||
        message.toLowerCase().includes('locked') ||
        message.toLowerCase().includes('too many login attempts');
      
      if (isLockout) {
        const match = message.match(/(\d+)\s*minute/i);
        const minutes = match ? parseInt(match[1], 10) : 15;
        setLockoutSeconds(minutes * 60);
        setError(message);
      } else if (message.toLowerCase().includes('email')) {
        setFormFieldError('email', { type: 'manual', message });
      } else if (message.toLowerCase().includes('password')) {
        setFormFieldError('password', { type: 'manual', message });
      } else {
        setError(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md p-6 sm:p-8 bg-white rounded-2xl shadow-xl">
      {showConsent && (
        <StudentConsentModal
          onAccepted={(updatedUser) => {
            if (updatedUser) updateUser(updatedUser);
            navigate('/dashboard');
          }}
        />
      )}
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">Welcome back</h2>
      <p className="text-sm sm:text-base text-gray-500 mb-6 sm:mb-8">Sign in to your Vet Collab account</p>
      
      {lockoutSeconds > 0 ? (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 text-amber-900 rounded-xl text-center shadow-sm">
          <div className="flex items-center justify-center gap-1.5 font-semibold text-amber-900 text-sm mb-1">
            <span>⚠️</span>
            <span>Account Temporarily Locked</span>
          </div>
          <p className="text-xs text-amber-800 mb-3">
            Too many failed login attempts (10 attempts). Please wait before trying again:
          </p>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white border border-amber-200 rounded-full font-mono text-lg font-bold text-amber-800 shadow-sm">
            <span>⏱️</span>
            <span>
              {String(Math.floor(lockoutSeconds / 60)).padStart(2, '0')}:
              {String(lockoutSeconds % 60).padStart(2, '0')}
            </span>
          </div>
          <div className="mt-3 text-xs text-amber-700">
            Forgot your password?{' '}
            <Link to="/forgot-password" className="font-semibold text-accent-indigo hover:underline">
              Reset password now
            </Link>
          </div>
        </div>
      ) : error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg text-center font-medium">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Email Address
          </label>
          <input
            {...register('email', { 
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address'
              },
              validate: {
                noEmoji: (val) => !/\p{Extended_Pictographic}/u.test(val) || 'Emojis are not allowed in email'
              }
            })}
            type="email"
            placeholder="your@email.com"
            disabled={isLoading}
            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-indigo focus:border-transparent transition-all disabled:opacity-50"
          />
          {errors.email && (
            <p className="mt-1 text-xs text-red-500 font-medium">{errors.email.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Password
          </label>
          <div className="relative">
            <input
              {...register('password', { 
                required: 'Password is required',
                validate: {
                  noEmoji: (val) => !/\p{Extended_Pictographic}/u.test(val) || 'Emojis are not allowed in password'
                }
              })}
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              disabled={isLoading}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-indigo focus:border-transparent transition-all disabled:opacity-50"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              disabled={isLoading}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1 text-xs text-red-500 font-medium">{errors.password.message}</p>
          )}
        </div>

        <div className="flex items-center justify-between">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input 
              {...register('rememberMe')}
              type="checkbox" 
              className="w-4 h-4 text-accent-indigo border-gray-300 rounded focus:ring-accent-indigo"
            />
            <span className="text-sm text-gray-600">Remember me</span>
          </label>
          <Link to="/forgot-password" className="text-sm font-semibold text-accent-indigo hover:opacity-80">
            Forgot password?
          </Link>
        </div>

        <button
          type="submit"
          disabled={isLoading || lockoutSeconds > 0}
          className="w-full py-3 px-4 bg-accent-indigo/80 text-white font-semibold rounded-lg hover:bg-accent-indigo focus:outline-none focus:ring-2 focus:ring-accent-indigo focus:ring-offset-2 transition-all disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <div className="flex items-center justify-center gap-2">
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span>Signing In...</span>
            </div>
          ) : lockoutSeconds > 0 ? (
            <span>Locked ({String(Math.floor(lockoutSeconds / 60)).padStart(2, '0')}:{String(lockoutSeconds % 60).padStart(2, '0')})</span>
          ) : 'Sign In'}
        </button>

        <div className="text-center pt-2">
          <p className="text-sm text-gray-600">
            Don't have an account?{' '}
            <Link to="/register" className="font-semibold text-accent-indigo hover:opacity-80">
              Sign up
            </Link>
          </p>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;
